// LoginApiService — thin fetch() wrapper around every engine endpoint
// the login flow needs. Stateless static methods; the orchestrator
// keeps the actual session in component state.
//
// Conventions match the chat-ui reference (ChatApiService.js):
//   - All calls use credentials:'include' so the Spring session cookie
//     is sent automatically.
//   - The /tt servlet prefix is part of every URL — apps are mounted
//     under that context root in production.
//   - Errors throw an Error whose .message is either the server's
//     JSON `{ message }` or the HTTP status. The orchestrator decides
//     how to translate that for the user.
//
// A single global `onUnauthorized` subscription list lets the host
// app (chat-ui's API service, ticketing's axios interceptor, ...)
// react when the session evaporates mid-page without each consumer
// having to overwrite the previous listener.

const TT = '/tt'

const unauthorizedListeners = new Set()

function notifyUnauthorized() {
  for (const fn of unauthorizedListeners) {
    try { fn() } catch (e) { /* swallow listener errors */ }
  }
}

async function request(url, options = {}) {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options
  })
  if (res.status === 401) {
    notifyUnauthorized()
  }
  return res
}

async function parseJsonOrThrow(res) {
  if (!res.ok) {
    let message = `HTTP ${res.status}`
    try {
      const body = await res.json()
      if (body && typeof body.message === 'string') message = body.message
      else if (body && typeof body.error === 'string') message = body.error
    } catch (e) { /* keep status fallback */ }
    const err = new Error(message)
    err.status = res.status
    throw err
  }
  return res.json().catch(() => null)
}

const LoginApiService = {

  // ---- Lifecycle / global hooks -----------------------------------

  /**
   * Register a callback fired whenever any request returns HTTP 401.
   * Returns an unsubscribe function. Multiple modules may subscribe;
   * the chat-ui reference originally OVERWROTE the single handler,
   * which made coexisting with axios-based hosts hard — we use a
   * Set so the library and the host can both listen.
   */
  onUnauthorized(fn) {
    if (typeof fn !== 'function') return () => {}
    unauthorizedListeners.add(fn)
    return () => unauthorizedListeners.delete(fn)
  },

  // ---- Session check ----------------------------------------------

  /**
   * GET /api/tibtix.com/login — returns a normalised session object
   * if the cookie still authenticates, otherwise null. Mirrors
   * ChatApiService.checkLogin() (out/chat-ui-src/.../ChatApiService.js).
   * Falls back to GET /api/userRoles when the login response omits
   * the role list (older builds).
   */
  async checkLogin() {
    const res = await request(`${TT}/api/tibtix.com/login`)
    if (!res.ok) return null
    const data = await res.json()
    if (!data || !data.loggedIn) return null

    let roles = Array.isArray(data.roles) ? data.roles : []
    if (!roles.length) {
      try {
        const rolesRes = await request(`${TT}/api/userRoles`)
        if (rolesRes.ok) {
          const parsed = await rolesRes.json()
          if (Array.isArray(parsed)) roles = parsed
        }
      } catch (e) { /* keep empty */ }
    }

    return normaliseSession(data, roles)
  },

  // ---- Login & logout ---------------------------------------------

  /**
   * POST /api/{domain}/login — `domain` is "tibtix.com" for the
   * default flow, or the engine's "default" alias which routes the
   * client login through the username-as-domain machinery.
   * The body keys (username/password/domainAccountName) match the
   * existing LoginCredentials model on the server.
   */
  async login({ username, password, domainAccountName, domain = 'tibtix.com' }) {
    const res = await request(`${TT}/api/${encodeURIComponent(domain)}/login`, {
      method: 'POST',
      body: JSON.stringify({ username, password, domainAccountName: domainAccountName || null })
    })
    // The /login endpoint returns 200 with loggedIn=false on bad
    // credentials, so we can't rely on res.ok alone — parse and let
    // the orchestrator branch on errorCode / lockedUntil.
    const data = await res.json().catch(() => null)
    if (!data) {
      const err = new Error(`HTTP ${res.status}`)
      err.status = res.status
      throw err
    }
    return data
  },

  /**
   * POST /api/tibtix.com/login with body.discover=true — discovery
   * login. Validates the credentials as a client-login on tibtix.com
   * and the response is enriched with two extra arrays:
   *
   *   - connections          : AccessibleAccount[] (Conn = customer)
   *   - adminDomainAccounts  : AccessibleAccount[] (admin role)
   *
   * Each AccessibleAccount carries { accountName, domainName,
   * displayName, imageUrl, kind }. The orchestrator uses these to
   * decide whether to show the role picker, an account picker, or
   * neither (auto-skip on a single option), and to render account
   * tiles with their profile image.
   *
   * On success the session is set up as a client on tibtix.com; the
   * orchestrator then either keeps that session (Client User path via
   * setClientContext()) or replaces it with a domain-bound session
   * (Domain Admin path via a second login() call with the chosen
   * domainAccountName=accountName##domain).
   */
  async discoverLogin({ username, password }) {
    const res = await request(`${TT}/api/tibtix.com/login`, {
      method: 'POST',
      body: JSON.stringify({ username, password, discover: true })
    })
    const data = await res.json().catch(() => null)
    if (!data) {
      const err = new Error(`HTTP ${res.status}`)
      err.status = res.status
      throw err
    }
    return data
  },

  /**
   * POST /api/ticketing/context — for a client user already logged in
   * via the tibtix.com client path, sets the active Domain-Account
   * context (which connection's tickets / chats are visible). No
   * password needed. Rejected server-side for domain-users — they
   * must re-login with a domainAccountName to switch.
   */
  async setClientContext({ accountName, domainName }) {
    const res = await request(`${TT}/api/ticketing/context`, {
      method: 'POST',
      body: JSON.stringify({ accountName, domainName })
    })
    return parseJsonOrThrow(res)
  },

  async logout() {
    await request(`${TT}/api/logout`).catch(() => null)
  },

  // ---- Password change & reset ------------------------------------

  async changePassword({ domain = 'tibtix.com', oldPassword, newPassword }) {
    const res = await request(
      `${TT}/api/${encodeURIComponent(domain)}/login/changePassword`,
      { method: 'POST', body: JSON.stringify({ oldPassword, newPassword }) }
    )
    return parseJsonOrThrow(res)
  },

  /**
   * POST /tt/view/forgotPasswordJson — initiates the "send me a new
   * password by email" flow. Body shape mirrors chat-ui (only the
   * email is required). Returns { success, message }.
   */
  async forgotPassword(email) {
    const res = await fetch(`${TT}/view/forgotPasswordJson`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    })
    const data = await res.json().catch(() => ({ success: false }))
    return data || { success: false }
  },

  // ---- Domain account picker --------------------------------------

  /**
   * POST /api/tib/getDomainAccountsByEmail — returns the list of
   * `account##domain` strings the email can log into as a domain
   * user. Used to populate the radio list in LoginView when the
   * client-toggle is off.
   */
  async getDomainAccountsByEmail(email) {
    const res = await request(`${TT}/api/tib/getDomainAccountsByEmail`, {
      method: 'POST',
      body: JSON.stringify({ email })
    })
    if (!res.ok) return []
    return res.json().catch(() => [])
  },

  // ---- TOTP / 2FA --------------------------------------------------

  /**
   * GET /api/totp/needTotp/{domain}/{account} — does this account
   * require a TOTP challenge for the current session? Returns
   * Boolean. Path params come from the just-authenticated session.
   */
  async needTotp(domain, account) {
    const res = await request(
      `${TT}/api/totp/needTotp/${encodeURIComponent(domain)}/${encodeURIComponent(account)}`
    )
    if (!res.ok) return false
    return res.json().catch(() => false)
  },

  /** GET /api/totp/hasTotpSecret — true if the user has already set up an authenticator. */
  async hasTotpSecret() {
    const res = await request(`${TT}/api/totp/hasTotpSecret`)
    if (!res.ok) return false
    return res.json().catch(() => false)
  },

  /**
   * GET /api/totp/checkTotp/{code} — 200/true on a valid code, 403
   * otherwise. We coerce both into a plain Boolean so the
   * orchestrator only has to branch once.
   */
  async checkTotp(code) {
    const res = await request(`${TT}/api/totp/checkTotp/${encodeURIComponent(code)}`)
    if (!res.ok) return false
    const data = await res.json().catch(() => false)
    return !!data
  },

  /**
   * GET /api/totp/createTotpSecretAndReturnGoogleAuthenticatorBarCodeIfNeeded
   * Returns the otpauth:// URL that an authenticator app can render
   * as a QR code. The library passes the string into a `qrserver.com`
   * <img> in TotpView when no client-side QR library is available.
   */
  async getTotpSetupUrl() {
    const res = await request(
      `${TT}/api/totp/createTotpSecretAndReturnGoogleAuthenticatorBarCodeIfNeeded`
    )
    if (!res.ok) return ''
    // Endpoint returns a plain string in the body
    return res.text().catch(() => '')
  },

  // ---- Terms of Use -----------------------------------------------

  /** GET /api/tib/terms — public payload {version, languages:{de,en}}. */
  async getTerms() {
    const res = await request(`${TT}/api/tib/terms`)
    if (!res.ok) return null
    return res.json().catch(() => null)
  },

  /**
   * GET /api/tib/termsStatus — authenticated. Tells us whether the
   * caller still has to accept the current terms version. Domain
   * users always get requiresAcceptance:false.
   */
  async getTermsStatus() {
    const res = await request(`${TT}/api/tib/termsStatus`)
    if (!res.ok) return null
    return res.json().catch(() => null)
  },

  /**
   * GET /login-ui/config.json — public runtime switches (no secrets).
   * clientUi: clients land in the new UI (/tt/ui) -> never pick a connection here.
   */
  async uiConfig() {
    // höchstens 3 s warten: ein hängender Aufruf darf die Anmeldung nicht blockieren (dann: alter Ablauf)
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timer = ctl ? setTimeout(() => ctl.abort(), 3000) : null
    try {
      const res = await fetch(`${TT}/login-ui/config.json`, { credentials: 'include', signal: ctl ? ctl.signal : undefined })
      if (!res.ok) return {}
      return await res.json().catch(() => ({}))
    } catch (e) {
      return {}
    } finally {
      if (timer) clearTimeout(timer)
    }
  },

  /**
   * GET /login-ui/announcement.json — vorgelagerter Hinweis (aus Resources, kein DB-Zugriff).
   * Schema: { id, enabled, mode:"info"|"blocking", de:{title,body[]}, en:{...} }. Leeres/Fehler -> {}.
   */
  async announcement() {
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timer = ctl ? setTimeout(() => ctl.abort(), 3000) : null
    try {
      const res = await fetch(`${TT}/login-ui/announcement.json`, { credentials: 'include', signal: ctl ? ctl.signal : undefined })
      if (!res.ok) return {}
      return await res.json().catch(() => ({}))
    } catch (e) {
      return {}
    } finally {
      if (timer) clearTimeout(timer)
    }
  },

  /** POST /api/tib/acceptTerms — records acceptance for the calling client user. */
  async acceptTerms(version) {
    const res = await request(`${TT}/api/tib/acceptTerms`, {
      method: 'POST',
      body: JSON.stringify({ version })
    })
    return parseJsonOrThrow(res)
  },

  // ---- Library-specific aggregate ---------------------------------

  /**
   * GET /api/login-ui/bootstrap — single aggregated probe that the
   * orchestrator fires once on mount. Backed by LoginUiBootstrap-
   * Controller on the server. Returns:
   *   {
   *     sessionValid, loggedInWithTempPassword,
   *     needsTotpSetup, hasTotpSecret,
   *     termsStatus: { currentVersion, acceptedVersion, requiresAcceptance, languages },
   *     postLoginRedirect
   *   }
   * If the endpoint is missing (older engine), we fall back to a
   * sequence of the individual probes — keeps the library forward-
   * compatible without requiring a backend redeploy.
   */
  async bootstrap() {
    const res = await fetch(`${TT}/api/login-ui/bootstrap`, { credentials: 'include' })
    if (res.ok) {
      return res.json().catch(() => null)
    }
    // Fallback for older engines
    const session = await this.checkLogin()
    if (!session) return { sessionValid: false }
    const termsStatus = await this.getTermsStatus()
    return {
      sessionValid: true,
      loggedInWithTempPassword: !!session.loggedInWithTempPassword,
      termsStatus,
      // We don't know the TOTP state without auth-bound probes — the
      // orchestrator handles that lazily after login.
    }
  }
}

/**
 * Normalises the raw LoginResponseModel into the shape the library
 * passes around as "session". Mirrors ChatApiService.checkLogin().
 */
function normaliseSession(data, roles) {
  // `domain-user` role is the canonical client-vs-domain
  // discriminator (consistent with HashhashAuthenticationToken's
  // isDomainUser() check on the server).
  const clientLogin = !roles.includes('domain-user')
  return {
    username: data.username,
    domain: data.domainName || data.domain || '',
    domainAccountName: data.accountName || data.domainAccountName || '',
    clientLogin,
    roles,
    loggedInWithTempPassword: !!data.loggedInWithTempPassword
  }
}

export default LoginApiService
export { TT, normaliseSession }
